/**
 * Shared helpers for re-deriving an OAuth connector's "discovered" account set
 * from its existing discovery endpoint, in stored-creds mode (#143).
 *
 * Extracted so the read-only "available but excluded" detail-view section
 * (#285) can reuse the wizard's discovery contract without duplicating the
 * parse/body/error logic. The wizard component
 * (OAuthPopulatedDropdownField.tsx) currently keeps its own copies of this
 * logic — folding it onto this module is a deferred follow-up so we don't
 * regress #143/#187/#189/#205.
 */
import { ApiError } from '@/lib/api/client';
import type { DropdownConfig } from '@/types/connector-schema';

export interface DiscoveryOption {
  value: string;
  label: string;
}

/** Values whose label is shared with another option (#2207). Those rows need
 *  a secondary id to be told apart; unique labels render unchanged. */
export function duplicateLabelValues(options: DiscoveryOption[]): Set<string> {
  const counts = new Map<string, number>();
  for (const o of options) counts.set(o.label, (counts.get(o.label) ?? 0) + 1);
  return new Set(options.filter((o) => (counts.get(o.label) ?? 0) > 1).map((o) => o.value));
}

/** Body keys whose values are credentials the server resolves from stored
 *  creds in stored-creds mode. Skipped when assembling the request body. #143
 *  `session_token` is the opaque pointer an OAuth /complete returns in place
 *  of the tokens (#2264). */
export const CREDENTIAL_BODY_KEYS = new Set(['refresh_token', 'access_token', 'session_token']);

/** Look up a dot-path within an object. Returns undefined if any step fails. */
export function resolvePath(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, part) => {
    if (acc && typeof acc === 'object' && part in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, obj);
}

export function hasValue(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.length > 0;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v as Record<string, unknown>).length > 0;
  return true;
}

/**
 * The single answer to "is this `depends_on` entry satisfied?" (#1289).
 *
 * Three rules, in order:
 *  1. Stored-creds mode: a dep mapped to a CREDENTIAL_BODY_KEY is satisfied —
 *     the server resolves it from stored creds, so the form never holds it (#143).
 *  2. A dep listed in `open_ended_depends_on` NEVER gates — it is satisfied
 *     whatever its value, absent included. Such a field's "everything" state
 *     is its *default*, so there is nothing to wait for: the discovery body
 *     simply omits the key (`buildStoredCredsBody` skips non-`hasValue`
 *     entries) and the backend enumerates the full set — `ListAccountsRequest.
 *     customer_ids` is `default_factory=list`, documented "empty = all
 *     accessible".
 *
 *     Checking for `[]` specifically is NOT enough, and that was the first
 *     fix attempt for #1264: `customer_ids` is `is_credential=true`, so
 *     `splitFormData` can only route it to the credentials blob, and it skips
 *     undefined/null/'' outright — while the `empty_means_all` auto-select
 *     seed is display-only and commits nothing (#1014/#1077). A wizard-created
 *     open-ended connector therefore stores the key in NEITHER blob, the
 *     editor re-seeds it as null from `config.settings`, and an
 *     `Array.isArray` test fails. Verified in a browser against a devstack:
 *     only a hand-written `settings.customer_ids: []` passed that test; the
 *     real absent/null shape stayed stuck on "Complete authentication above".
 *
 *     Safety: this does not open discovery before OAuth. The credential dep
 *     (`oauth_connect`) is a separate `depends_on` entry, still gated by
 *     rule 1 in stored-creds mode and by rule 3 in the wizard.
 *  3. Otherwise the dep needs a non-empty value.
 */
export function satisfiesDependency(
  name: string,
  config: DropdownConfig,
  allValues: Record<string, unknown>,
  storedCredsMode: boolean,
): boolean {
  if (storedCredsMode) {
    const usedAsCredential = Object.entries(config.request_body_map).some(
      ([bodyKey, fieldPath]) =>
        CREDENTIAL_BODY_KEYS.has(bodyKey) && fieldPath.startsWith(name + '.'),
    );
    if (usedAsCredential) return true;
  }
  if (config.open_ended_depends_on?.includes(name)) return true;
  return hasValue(allValues[name]);
}

/**
 * Normalize a persisted selection value into a `string[]`. Selections are
 * stored as `string[]` (multi), a comma-separated string (legacy CSV
 * connectors), or a single string (single-select). null/undefined → [].
 */
export function normalizeSelection(value: unknown): string[] {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

/**
 * Build the stored-creds POST body for a discovery fetch: always sets
 * `connector_name`, plus any non-credential `request_body_map` entries resolved
 * from the connector's persisted settings (credential body keys are skipped;
 * the server looks those up from stored creds). Mirrors the wizard's
 * stored-creds body assembly so connectors with a non-credential dependency
 * (e.g. a `global_company_id`-style scope) work too.
 */
export function buildStoredCredsBody(
  config: DropdownConfig,
  connectorName: string,
  settings: Record<string, unknown>,
): Record<string, unknown> {
  const body: Record<string, unknown> = { connector_name: connectorName };
  for (const [bodyKey, fieldPath] of Object.entries(config.request_body_map)) {
    if (CREDENTIAL_BODY_KEYS.has(bodyKey)) continue;
    const v = resolvePath(settings, fieldPath);
    if (hasValue(v)) body[bodyKey] = v;
  }
  return body;
}

/** Map a discovery response into `{ value, label }[]` per the config keys.
 *  Label falls back to the value when the label key is empty/missing. */
export function parseDiscoveryOptions(data: unknown, config: DropdownConfig): DiscoveryOption[] {
  const items = resolvePath(data, config.response_path);
  if (!Array.isArray(items)) return [];
  return items.map((item) => {
    const rec = item as Record<string, unknown>;
    const value = String(rec[config.value_key] ?? '');
    const rawLabel = rec[config.label_key];
    const label =
      rawLabel !== null && rawLabel !== undefined && String(rawLabel).length > 0
        ? String(rawLabel)
        : value;
    return { value, label };
  });
}

export function extractErrorCode(err: unknown): string | null {
  if (err instanceof ApiError && err.details && typeof err.details === 'object') {
    const code = (err.details as { error_code?: unknown }).error_code;
    return typeof code === 'string' ? code : null;
  }
  return null;
}

/** Generic fallback shown when no usable message can be recovered from the error.
 *  Guards against the axios interceptor's "[object Object]" message (it passes a
 *  structured `data.message` dict straight to `new ApiError`, so `err.message`
 *  stringifies to "[object Object]" and the friendly nested text is dropped). */
const GENERIC_DISCOVERY_ERROR =
  "Couldn't load available accounts. Check the connection and try again.";

/** Dig a human-readable string out of an ApiError's `details`, tolerating the
 *  several shapes the backend/interceptor produce: a string detail, a
 *  `{ message: string }`, or a nested `{ message: { message: string } }`. */
function extractDetailMessage(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const d = err.details;
  if (typeof d === 'string' && d.length > 0) return d;
  if (d && typeof d === 'object') {
    const msg = (d as { message?: unknown }).message;
    if (typeof msg === 'string' && msg.length > 0) return msg;
    if (msg && typeof msg === 'object') {
      const nested = (msg as { message?: unknown }).message;
      if (typeof nested === 'string' && nested.length > 0) return nested;
    }
  }
  return null;
}

export interface ErrorMessageOptions {
  /** Inserted after "Reconnect" in the stored-credentials message, e.g. "above".
   *  Omitted -> location-neutral "Click Reconnect to refresh." (detail-view default). */
  reconnectHint?: string;
}

export function errorMessageForCode(
  code: string | null,
  err: unknown,
  opts?: ErrorMessageOptions,
): string {
  switch (code) {
    case 'connector_not_found':
      return "This connector's saved configuration couldn't be found. Try refreshing the page.";
    case 'stored_credentials_corrupt':
    case 'stored_credentials_incomplete': {
      const where = opts?.reconnectHint ? ` ${opts.reconnectHint}` : '';
      return `Saved credentials are unreadable. Click Reconnect${where} to refresh.`;
    }
    default: {
      const detailMsg = extractDetailMessage(err);
      if (detailMsg) return detailMsg;
      // Never surface the interceptor's "[object Object]" placeholder.
      if (err instanceof Error && err.message && err.message !== '[object Object]') {
        return err.message;
      }
      return GENERIC_DISCOVERY_ERROR;
    }
  }
}
